// BusinessEditor — full editor surface (US-106..US-117).
//
// Eight sections wired against `useBusiness(id)` + `useUpdateBusiness`:
//   - Identity (name, description, website, socials)               US-106
//   - Personas                                                       US-107
//   - Pains + Value props                                            US-108
//   - Hot signals                                                    US-109
//   - Contact (email, phone, calendar URL, priority)                 US-110
//   - Pricing (nature, models, products, policy)                     US-111
//   - AI rules (question guide, banned words, sales rules)           US-112
//
// Per-field AI assist (US-113) consumes the real `useBusinessAiField` SSE
// stream. Incremental chunks land into the local draft state via `setDraft`.
//
// Optimistic save (US-117): every mutation toasts. We expose a top-bar
// "Save" + a footer "Save business" — both call the same mutation.
//
// US-114..US-116: AI scan modal lives in its own file; we render the
// banner here and let the modal own its state machine.

import { Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  useBusiness,
  useBusinessAiField,
  useBusinessImpact,
  useDeleteBusiness,
  useUpdateBusiness,
  type Business,
  type BusinessAiFieldProfile,
  type BusinessScanField,
} from '@barel/api-client/react';
import { businessFormSchema } from '@barel/core/schemas';
import { useT } from '@barel/i18n';
import { Banner, Button, Card, PageHeader, toast } from '@barel/ui';

import { runMutationWithToast } from '@/lib/mutationToast';
import { useListEditor } from '@/lib/useListEditor';

import { BusinessAiScanModal } from './BusinessAiScanModal';
import { DeleteBusinessModal } from './DeleteBusinessModal';
import { DEFAULT_BUSINESS, resolveContactPriority, type BusinessDraft } from './lib/defaults';
import { AccountBindingSection } from './sections/AccountBindingSection';
import { AiRulesSection } from './sections/AiRulesSection';
import { ContactSection } from './sections/ContactSection';
import { HotSignalsSection } from './sections/HotSignalsSection';
import { IdentitySection } from './sections/IdentitySection';
import { PainsSection } from './sections/PainsSection';
import { PersonasSection } from './sections/PersonasSection';
import { PricingSection } from './sections/PricingSection';
import { ValuePropsSection } from './sections/ValuePropsSection';

export interface BusinessEditorProps {
  id: string;
  /** Where to navigate after a successful delete. */
  onDeleted: () => void;
  /** Where to navigate when the user clicks "Back to list". */
  onBack: () => void;
}

type AiField =
  | 'description'
  | 'personas'
  | 'pains'
  | 'valueProps'
  | 'salesRules'
  | 'questionGuide'
  | 'hotSignals';

type StructuredAiValue = BusinessDraft['personas'] | string[];

function parseStringArray(value: unknown): string[] | null {
  if (!Array.isArray(value) || !value.every((item) => typeof item === 'string')) return null;
  const items = value.map((item) => item.trim()).filter(Boolean);
  return items.length > 0 ? items : null;
}

function parsePersonas(value: unknown): BusinessDraft['personas'] | null {
  if (!Array.isArray(value)) return null;
  const personas: BusinessDraft['personas'] = [];
  for (const item of value) {
    if (
      !item ||
      typeof item !== 'object' ||
      !('title' in item) ||
      typeof item.title !== 'string' ||
      !('desc' in item) ||
      typeof item.desc !== 'string'
    ) {
      return null;
    }
    const title = item.title.trim();
    if (!title) return null;
    personas.push({ title, desc: item.desc.trim() });
  }
  return personas.length > 0 ? personas : null;
}

/**
 * Prefer typed arrays from the terminal SSE frame. During a rolling deploy,
 * accept strict JSON arrays and finally the legacy comma/newline response.
 */
function parseStructuredAiOutput(
  field: Exclude<AiField, 'description'>,
  terminalValue: unknown,
  buffer: string,
): StructuredAiValue | null {
  const parseTyped = (value: unknown): StructuredAiValue | null =>
    field === 'personas' ? parsePersonas(value) : parseStringArray(value);

  if (terminalValue !== undefined) return parseTyped(terminalValue);

  const trimmed = buffer.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('[')) {
    try {
      return parseTyped(JSON.parse(trimmed));
    } catch {
      return null;
    }
  }

  const legacyItems = trimmed
    .split(/,|\n/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (legacyItems.length === 0) return null;
  if (field === 'personas') {
    return legacyItems.map((title) => ({ title, desc: '' }));
  }
  return legacyItems;
}

function toDraft(business: Business): BusinessDraft {
  // Strip server-only metadata from the draft. The shape is stable per the
  // `Business` interface in api-client; we extract only the editable subset.
  const {
    id: _id,
    userId: _userId,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    archivedAt: _archivedAt,
    ...rest
  } = business;
  return rest;
}

const businessEditorSchema = businessFormSchema.omit({ userId: true, id: true });

function editablePayload(draft: BusinessDraft) {
  const {
    lastScanRunId: _lastScanRunId,
    lastScanSuggestion: _lastScanSuggestion,
    ...editable
  } = draft;
  return editable;
}

function toAiProfile(draft: BusinessDraft): BusinessAiFieldProfile {
  return {
    name: draft.name,
    description: draft.description,
    website: draft.website,
    personas: draft.personas,
    pains: draft.pains,
    valueProps: draft.valueProps,
    hotSignals: draft.hotSignals,
    businessNature: draft.businessNature,
    pricingModels: draft.pricingModels,
    pricingProducts: draft.pricingProducts,
    pricingPolicy: draft.pricingPolicy,
    questionGuide: draft.questionGuide,
    bannedWords: draft.bannedWords,
    salesRules: draft.salesRules,
  };
}

export function BusinessEditor({ id, onDeleted, onBack }: BusinessEditorProps) {
  const t = useT('businesses');

  const query = useBusiness(id);
  const update = useUpdateBusiness();
  const del = useDeleteBusiness();
  const aiField = useBusinessAiField();

  const [draft, setDraft] = useState<BusinessDraft>(DEFAULT_BUSINESS);
  const [baseline, setBaseline] = useState<BusinessDraft>(DEFAULT_BUSINESS);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [aiBusyField, setAiBusyField] = useState<string | null>(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const aiAbortRef = useRef<AbortController | null>(null);

  // Hydrate once per route id. TanStack Router can reuse this component when
  // navigating directly between two business detail URLs, so a boolean would
  // leave the previous business rendered under the new URL.
  useEffect(() => {
    if (!query.data || query.data.business.id !== id || loadedId === id) return;
    const loaded = toDraft(query.data.business);
    setDraft(loaded);
    setBaseline(loaded);
    setValidationError(null);
    setScanOpen(false);
    setDeleteOpen(false);
    setLoadedId(id);
  }, [id, loadedId, query.data]);

  // Keep contact priority valid when channels change.
  useEffect(() => {
    setDraft((prev) => ({
      ...prev,
      contactPriority: resolveContactPriority(prev),
    }));
  }, [draft.calendarUrl, draft.phone]);

  const businessLoaded = loadedId === id;
  const isDirty = useMemo(
    () => JSON.stringify(editablePayload(draft)) !== JSON.stringify(editablePayload(baseline)),
    [baseline, draft],
  );

  useEffect(() => {
    if (!isDirty) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const onChange = <K extends keyof BusinessDraft>(key: K, value: BusinessDraft[K]) => {
    setValidationError(null);
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  // ─── Save ────────────────────────────────────────────────────────────────

  const onSave = async (): Promise<void> => {
    const parsed = businessEditorSchema.safeParse(editablePayload(draft));
    if (!parsed.success) {
      setValidationError(t('detail.validationError'));
      toast.error({ title: t('detail.validationError') });
      return;
    }
    await runMutationWithToast(() => update.mutateAsync({ id, patch: parsed.data }), {
      success: { title: t('detail.savedToast') },
      onSuccess: (result) => {
        const saved = toDraft(result.business);
        setDraft(saved);
        setBaseline(saved);
        setValidationError(null);
      },
      error: { title: t('detail.saveErrorToast') },
    });
  };

  const onBackSafely = (): void => {
    if (!isDirty || window.confirm(t('detail.unsavedConfirm'))) onBack();
  };

  // ─── Delete ──────────────────────────────────────────────────────────────

  const onConfirmDelete = (): Promise<void> =>
    runMutationWithToast(() => del.mutateAsync({ id }), {
      success: { title: t('delete.deletedToast') },
      onSuccess: () => {
        setDeleteOpen(false);
        onDeleted();
      },
      error: { title: t('delete.errorToast') },
    });

  // ─── Per-field AI assist (US-113) ────────────────────────────────────────
  //
  // Opens the SSE stream and appends description deltas progressively.
  // Structured fields prefer the typed terminal value, with JSON and the
  // legacy comma/newline payload retained for rolling-deploy compatibility.

  const runAi = async (field: AiField): Promise<void> => {
    if (aiBusyField) return;
    setAiBusyField(field);
    aiAbortRef.current?.abort();
    const controller = new AbortController();
    aiAbortRef.current = controller;
    let buffer = '';
    let terminalValue: unknown = undefined;
    let sawDone = false;
    let userFacingError: string | null = null;
    const originalValue = draft[field];
    try {
      const stream = await aiField.open({
        id,
        field,
        current:
          typeof draft[field] === 'string'
            ? (draft[field] as string)
            : JSON.stringify(draft[field]),
        profile: toAiProfile(draft),
        signal: controller.signal,
      });
      let firstChunk = true;
      for await (const frame of stream) {
        if (controller.signal.aborted) break;
        if (frame.error) {
          userFacingError = frame.error.message || t('ai.errorToast');
          throw new Error('AI field stream failed');
        }
        if ('value' in frame) terminalValue = frame.value;
        if (frame.delta) {
          buffer += frame.delta;
          if (field === 'description') {
            // Replace progressively for short scalar fields.
            setDraft((prev) => ({
              ...prev,
              description: firstChunk
                ? (frame.delta ?? '')
                : (prev.description ?? '') + (frame.delta ?? ''),
            }));
            firstChunk = false;
          }
        }
        if (frame.done) {
          sawDone = true;
          break;
        }
      }

      if (controller.signal.aborted || !sawDone) throw new Error(t('ai.errorToast'));

      if (field === 'description') {
        const finalDescription = typeof terminalValue === 'string' ? terminalValue : buffer;
        if (!finalDescription.trim()) throw new Error('AI field stream produced no output');
        // The terminal value is authoritative; older streams have already
        // rendered the same buffered value progressively.
        if (typeof terminalValue === 'string') {
          const description = terminalValue;
          setDraft((prev) => ({ ...prev, description }));
        }
      } else {
        const parsed = parseStructuredAiOutput(field, terminalValue, buffer);
        if (!parsed) throw new Error('AI field stream produced invalid output');
        setDraft((prev) => ({ ...prev, [field]: parsed }) as BusinessDraft);
      }
    } catch {
      setDraft((prev) => ({ ...prev, [field]: originalValue }) as BusinessDraft);
      toast.error({
        // Provider/client diagnostics are not user-facing and can expose
        // implementation details. Server SSE errors already carry a safe,
        // localized message; all other failures use the generic product copy.
        title: userFacingError ?? t('ai.errorToast'),
      });
    } finally {
      setAiBusyField(null);
    }
  };

  // ─── Socials helpers ─────────────────────────────────────────────────────

  const socials = useListEditor(draft.socials, (next) => onChange('socials', next));
  const updateSocial = (
    idx: number,
    patch: Partial<{ kind: string; url: string; label: string }>,
  ) => socials.updateAt(idx, (s) => ({ ...s, ...patch }));
  const removeSocial = (idx: number) => socials.removeAt(idx);
  const addSocial = () => socials.add({ kind: 'other', label: '', url: '' });

  // ─── Pricing products helpers ────────────────────────────────────────────

  const products = useListEditor(draft.pricingProducts, (next) =>
    onChange('pricingProducts', next),
  );
  const addProduct = () => products.add({ name: '', price: '', note: '' });
  const updateProduct = (
    idx: number,
    patch: Partial<{ name: string; price: string; note: string }>,
  ) => products.updateAt(idx, (p) => ({ ...p, ...patch }));
  const removeProduct = (idx: number) => products.removeAt(idx);

  const togglePricingModel = (key: string) =>
    setDraft((prev) => ({
      ...prev,
      pricingModels: prev.pricingModels.includes(key)
        ? prev.pricingModels.filter((k) => k !== key)
        : [...prev.pricingModels, key],
    }));

  const onScanApplied = (business: Business, appliedFields: BusinessScanField[]): void => {
    const mergeAppliedFields = (prev: BusinessDraft): BusinessDraft => {
      const next = { ...prev } as Record<string, unknown>;
      const appliedBusiness = business as unknown as Record<string, unknown>;
      for (const field of appliedFields) next[field] = appliedBusiness[field];
      return next as BusinessDraft;
    };
    setDraft(mergeAppliedFields);
    setBaseline(mergeAppliedFields);
  };

  // ─── Compute delete-impact via /businesses/:id/impact (Wave C2) ──────────
  //
  // Fetch impact only when it is needed and refresh it on every open. Unknown
  // impact must never be represented as zero because that could enable a
  // destructive confirmation from incomplete data.
  const impactQuery = useBusinessImpact(id, { enabled: deleteOpen });
  const { refetch: refetchImpact } = impactQuery;
  useEffect(() => {
    if (deleteOpen) void refetchImpact();
  }, [deleteOpen, refetchImpact]);

  // Surface a fetch failure first — otherwise `!businessLoaded` (never set on
  // error) would keep the loader spinning forever and the error branch below
  // would be unreachable.
  if (query.isError) {
    return (
      <Banner tone="error" data-testid="business-editor-error">
        {t('detail.saveErrorToast')}
      </Banner>
    );
  }

  if (query.isLoading || !businessLoaded) {
    return (
      <div className="flex flex-col gap-4 p-6" data-testid="business-editor-loading">
        <span className="text-sm text-secondary">{t('detail.loading')}</span>
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-6" data-testid="business-editor">
      <PageHeader
        title={draft.name || t('detail.title')}
        subtitle={t('detail.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBackSafely}
              data-testid="business-editor-back"
            >
              {t('detail.back')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!isDirty}
              loading={update.isPending}
              onClick={() => void onSave()}
              data-testid="business-editor-save-header"
            >
              {t('detail.saveHeader')}
            </Button>
          </div>
        }
      />

      {validationError ? (
        <Banner tone="error" data-testid="business-editor-validation-error">
          {validationError}
        </Banner>
      ) : null}

      {/* AI scan banner (US-114) */}
      <Card padding="lg" radius="2xl">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <strong className="text-primary">{t('ai.scanBannerTitle')}</strong>
            <span className="text-sm text-secondary">{t('ai.scanBannerSubtitle')}</span>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setScanOpen(true)}
            data-testid="business-editor-scan-cta"
          >
            {t('ai.scanBannerCta')}
          </Button>
        </div>
      </Card>

      {/* ─── Identity (US-106) ─────────────────────────────────────── */}
      <IdentitySection
        name={draft.name}
        description={draft.description}
        website={draft.website}
        socials={draft.socials}
        onChange={onChange}
        updateSocial={updateSocial}
        removeSocial={removeSocial}
        addSocial={addSocial}
        runAi={runAi}
        aiBusyField={aiBusyField}
      />

      <AccountBindingSection linkedinAccountId={draft.linkedinAccountId} onChange={onChange} />

      {/* ─── Personas (US-107) ─────────────────────────────────────── */}
      <PersonasSection
        personas={draft.personas}
        onChange={onChange}
        runAi={runAi}
        aiBusyField={aiBusyField}
      />

      {/* ─── Pains (US-108) ───────────────────────────────────────── */}
      <PainsSection
        pains={draft.pains}
        onChange={onChange}
        runAi={runAi}
        aiBusyField={aiBusyField}
      />

      {/* ─── Value props (US-108) ─────────────────────────────────── */}
      <ValuePropsSection
        valueProps={draft.valueProps}
        onChange={onChange}
        runAi={runAi}
        aiBusyField={aiBusyField}
      />

      {/* ─── Hot signals (US-109) ─────────────────────────────────── */}
      <HotSignalsSection
        hotSignals={draft.hotSignals}
        onChange={onChange}
        runAi={runAi}
        aiBusyField={aiBusyField}
      />

      {/* ─── Contact (US-110) ─────────────────────────────────────── */}
      <ContactSection
        email={draft.email}
        phone={draft.phone}
        calendarUrl={draft.calendarUrl}
        contactPriority={draft.contactPriority}
        onChange={onChange}
      />

      {/* ─── Pricing (US-111) ─────────────────────────────────────── */}
      <PricingSection
        businessNature={draft.businessNature}
        pricingModels={draft.pricingModels}
        pricingProducts={draft.pricingProducts}
        pricingPolicy={draft.pricingPolicy}
        onChange={onChange}
        togglePricingModel={togglePricingModel}
        addProduct={addProduct}
        updateProduct={updateProduct}
        removeProduct={removeProduct}
      />

      {/* ─── AI rules (US-112) ────────────────────────────────────── */}
      <AiRulesSection
        questionGuide={draft.questionGuide}
        bannedWords={draft.bannedWords}
        salesRules={draft.salesRules}
        onChange={onChange}
        runAi={runAi}
        aiBusyField={aiBusyField}
      />

      {/* Footer actions */}
      <div className="flex items-center justify-between gap-2">
        <Button
          variant="danger"
          size="md"
          iconLeft={<Trash2 size={14} aria-hidden="true" />}
          onClick={() => setDeleteOpen(true)}
          data-testid="business-editor-delete"
        >
          {t('detail.deleteCta')}
        </Button>
        <Button
          variant="primary"
          size="md"
          disabled={!isDirty}
          loading={update.isPending}
          onClick={() => void onSave()}
          data-testid="business-editor-save-footer"
        >
          {t('detail.save')}
        </Button>
      </div>

      <DeleteBusinessModal
        name={draft.name}
        impact={impactQuery.data}
        loading={impactQuery.isLoading || impactQuery.isFetching}
        error={impactQuery.isError}
        onRetry={() => void refetchImpact()}
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => void onConfirmDelete()}
        pending={del.isPending}
      />

      <BusinessAiScanModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        businessId={id}
        initialWebsite={draft.website}
        initialLinkedinCompany={draft.socials.find((s) => s.kind === 'linkedin')?.url ?? ''}
        currentValues={draft}
        onApplied={onScanApplied}
      />
    </section>
  );
}
